import { jwtDecode } from 'jwt-decode';
import config from '../../../config';
import useDeviceId from './useDeviceId';
import useIOS from './useIOS';
import { checkIfUserHasAccessToLocation } from '../../../accessControl';
import { setAuthenticationUpdatedCookie } from '../../../cookie';
import { retryDelay, sleep } from '../../../retry';

type Credentials = { username: string; password: string };

type DecodedJwt = { exp: number };
type RefreshTokenResponse = { refreshToken?: string };

const useAuthV3 = (props: {
  onSuccess: (refreshToken: string) => void;
  onFailure: () => void;
}) => {
  const deviceId = useDeviceId();
  const { iosAppVersion } = useIOS();
  const locationId = new URLSearchParams(window.location.search).get(
    'location_id'
  );

  const handleLogin = async (credentials: Credentials) => {
    const loginResponse = await login({ ...credentials, deviceId });
    if (loginResponse) {
      if (iosAppVersion && locationId) {
        const userHasAccessToLocation = checkIfUserHasAccessToLocation(
          loginResponse.accessToken,
          Number(locationId)
        );

        if (!userHasAccessToLocation) {
          return props.onFailure();
        }
      }

      localStorage.setItem('REFRESH_TOKEN', loginResponse.refreshToken);
      return props.onSuccess(loginResponse.refreshToken);
    }
    return props.onFailure();
  };

  /**
   * Refresh the stored token, waiting out an unreachable server instead of logging out.
   *
   * Only a refusal clears the token. A network error, a 5xx or a 429 says nothing about the token,
   * and clearing it on one would log a terminal out over a DNS blip.
   */
  const checkTokenInLocalStorage = async (options: {
    onUnreachable: () => void;
    isCancelled: () => boolean;
  }) => {
    const token = localStorage.getItem('REFRESH_TOKEN');

    if (!token || hasTokenExpired(token)) {
      clearStoredRefreshToken();
      return props.onFailure();
    }

    for (let attempt = 0; ; attempt++) {
      const result = await validateStoredRefreshToken(token, deviceId);
      if (options.isCancelled()) {
        return;
      }

      if (result.outcome === 'refreshed') {
        localStorage.setItem('REFRESH_TOKEN', result.token);
        return props.onSuccess(result.token);
      }

      if (result.outcome === 'rejected') {
        clearStoredRefreshToken();
        return props.onFailure();
      }

      options.onUnreachable();
      await sleep(retryDelay(attempt));
      if (options.isCancelled()) {
        return;
      }
    }
  };

  return { login: handleLogin, checkTokenInLocalStorage };
};

const clearStoredRefreshToken = () => {
  localStorage.removeItem('REFRESH_TOKEN');
  setAuthenticationUpdatedCookie();
};

const hasTokenExpired = (token: string | null) => {
  if (!token?.length) {
    return true;
  }

  try {
    const decodedToken = jwtDecode<DecodedJwt>(token);
    const { exp: expirationTime } = decodedToken;
    const now = Date.now() / 1000;
    return expirationTime < now;
  } catch (e) {
    console.error(e);
    return true;
  }
};

const login = async (credentials: {
  username: string;
  password: string;
  deviceId: string;
}) => {
  const loginResponse = await loginFetch(
    `${config.baseUrl}/v2/login`,
    credentials
  );

  if (!loginResponse.ok) {
    return false;
  }

  const json = await loginResponse.json();

  if (!(json as LoginResponse).refreshToken) {
    return false;
  }

  const jwtResponse: LoginResponse = json as LoginResponse;

  return jwtResponse;
};

const loginFetch = async (
  url: string,
  credentials: { username: string; password: string }
) => {
  const postData = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
  };
  return fetch(url, postData);
};

type RefreshResult =
  | { outcome: 'refreshed'; token: string }
  | { outcome: 'rejected' }
  | { outcome: 'unreachable' };

const validateStoredRefreshToken = async (
  token: string,
  deviceId?: string
): Promise<RefreshResult> => {
  try {
    const refreshResponse = await fetch(`${config.ordrUrl}/v1/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
        ...(deviceId ? { deviceId } : {}),
      }),
    });

    if (refreshResponse.status >= 500 || refreshResponse.status === 429) {
      return { outcome: 'unreachable' };
    }

    if (!refreshResponse.ok) {
      return { outcome: 'rejected' };
    }

    const json = (await refreshResponse.json()) as RefreshTokenResponse;
    return { outcome: 'refreshed', token: json.refreshToken || token };
  } catch {
    // Network failure, or a 200 that was not ours (a captive portal, say).
    return { outcome: 'unreachable' };
  }
};

type LoginResponse = {
  accessToken: string;
  refreshToken: string;
  pin?: string;
};

export default useAuthV3;
